import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import FormInput from "../../components/FormInput";
import Button from "../../components/Button";
import Toast from "../../components/Toast";
import ActionLoader from "../../components/ActionLoader";
import ImageUploading from "../../components/AddEditItems/ImageUpload";
import DriverLicenseScanner from "../../components/DriverLicenseScanner";

import {
  SIGNIN_IN_URL,
  SIGNUP_URL,
  postData,
  GET_USER_SIGNIN_LOGO,
  STATES_LIST,
} from "../../utils/api";
import {
  uploadSignupImage,
  SIGNUP_IMAGE_UPLOAD_TYPE_PROFILE,
  SIGNUP_IMAGE_UPLOAD_TYPE_DRIVER_LICENSE,
  SIGNUP_IMAGE_UPLOAD_TYPE_BUSINESS_LICENSE,
} from "../../utils/signupImageUpload";

import Logo1 from "../../icons/abcattech.png";
import Logo2 from "../../icons/newicons/bottomSidebarLogo.png";
import BG from "../../icons/bg.png";
import EYE from "../../icons/eye.png";
import CaretIcon from "../../icons/caret.png";

import { getToken } from "../../utils/storage";
import useIsMobile from "../../hooks/useIsMobile";

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

const normalizeInput = (value) => {
  if (!value) return value;
  const currentValue = value.replace(/[^\d]/g, "");
  const cvLength = currentValue.length;

  if (value.length > 0) {
    if (cvLength < 4) return currentValue;
    if (cvLength < 7)
      return `${currentValue.slice(0, 3)}-${currentValue.slice(3)}`;
    return `${currentValue.slice(0, 3)}-${currentValue.slice(
      3,
      6
    )}-${currentValue.slice(6, 10)}`;
  }
};

export const Signup = (props) => {
  const isMobile = useIsMobile();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [notes, setNotes] = useState("");
  const [smsNumber, setSmsNumber] = useState("");
  const [driversLicense, setDriversLicense] = useState("");
  const [ein, setEin] = useState("");
  const [businessLicense, setBusinessLicense] = useState("");
  const [businessLicenseFiles, setBusinessLicenseFiles] = useState(null);

  const [seePassword, setSeePassword] = useState(false);
  const [seeConfirmPassword, setSeeConfirmPassword] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(Logo1);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [image, setImage] = useState(null);
  const [driversLicenseUrl, setDriversLicenseUrl] = useState(null);

  const [toastParams, setToastParams] = useState({
    message: "",
    type: "",
    id: "",
  });

  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const setLogo1 = () => {
    const hostname = window.location.hostname;
    const isAllowedHost = hostname.includes("cattraders.com");
    if (isAllowedHost) {
      setCompanyLogo(null);
    } else {
      setCompanyLogo(Logo1);
    }
  };

  useEffect(() => {
    setLogo1();
  }, []);

  // Helper function to check if all required fields are valid (without showing toast)
  const areAllRequiredFieldsValid = () => {
    const trimmedEmail = email?.trim();
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      return false;
    }
    if (!password || password.length < 6) {
      return false;
    }
    if (password !== confirmPassword) {
      return false;
    }
    if (!firstName || firstName.trim().length < 2) {
      return false;
    }
    if (!lastName || lastName.trim().length < 2) {
      return false;
    }
    if (!phone) {
      return false;
    }
    if (!address) {
      return false;
    }
    if (!city) {
      return false;
    }
    if (!state) {
      return false;
    }
    if (!zipcode) {
      return false;
    }
    if (!driversLicense || driversLicense.trim().length === 0) {
      return false;
    }
    return true;
  };

  const handleEmailBlur = async () => {
    const trimmedEmail = email?.trim();
    
    // Don't make API call if email is empty or just whitespace
    if (!trimmedEmail || trimmedEmail.length === 0) {
      setLogo1();
      setIsEmailValid(false);
      return;
    }

    // Don't make API call if email is invalid
    if (!isValidEmail(trimmedEmail)) {
      setLogo1();
      setIsEmailValid(false);
      return;
    }

    // Only make API call if all required fields are valid
    if (!areAllRequiredFieldsValid()) {
      setLogo1();
      setIsEmailValid(false);
      return;
    }

    // Only make API call if email is valid and all required fields are filled
    setIsLoading(true);
    try {
      const logoData = await postData(GET_USER_SIGNIN_LOGO, {
        email: trimmedEmail,
      });
      if (logoData?.companyLogo) {
        setCompanyLogo(logoData?.companyLogo);
      }
      setIsEmailValid(true);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      setIsEmailValid(false);
      setToastParams({
        type: "ERROR",
        message: e && e.error ? e.error : "Oops! Please Enter valid email id",
        id: new Date().getTime(),
      });
    }
  };

  const validateFields = () => {
    const trimmedEmail = email?.trim();
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setToastParams({
        type: "ERROR",
        message: "Please enter a valid email address",
        id: new Date().getTime(),
      });
      return false;
    }
    if (!password || password.length < 6) {
      setToastParams({
        type: "ERROR",
        message: "Password must be at least 6 characters long",
        id: new Date().getTime(),
      });
      return false;
    }
    if (password !== confirmPassword) {
      setToastParams({
        type: "ERROR",
        message: "Passwords do not match",
        id: new Date().getTime(),
      });
      return false;
    }
    if (!firstName || firstName.trim().length < 2) {
      setToastParams({
        type: "ERROR",
        message: "First Name must be at least 2 characters long",
        id: new Date().getTime(),
      });
      return false;
    }
    if (!lastName || lastName.trim().length < 2) {
      setToastParams({
        type: "ERROR",
        message: "Last Name must be at least 2 characters long",
        id: new Date().getTime(),
      });
      return false;
    }
    if (!phone) {
      setToastParams({
        type: "ERROR",
        message: "Phone is required",
        id: new Date().getTime(),
      });
      return false;
    }
    if (!address) {
      setToastParams({
        type: "ERROR",
        message: "Address is required",
        id: new Date().getTime(),
      });
      return false;
    }
    if (!city) {
      setToastParams({
        type: "ERROR",
        message: "City is required",
        id: new Date().getTime(),
      });
      return false;
    }
    if (!state) {
      setToastParams({
        type: "ERROR",
        message: "State is required",
        id: new Date().getTime(),
      });
      return false;
    }
    if (!zipcode) {
      setToastParams({
        type: "ERROR",
        message: "Zipcode is required",
        id: new Date().getTime(),
      });
      return false;
    }
    if (!driversLicense || driversLicense.trim().length === 0) {
      setToastParams({
        type: "ERROR",
        message: "Driver License is required",
        id: new Date().getTime(),
      });
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHasSubmitted(true);

    if (!validateFields()) {
      return;
    }

    setIsLoading(true);
    try {
      let profileUrl = "";
      let driversLicenseUrlValue = "";
      let businessLicenseUrlValue = "";

      // Upload profile image if provided
      if (image && image.length > 0 && image[0].file) {
        try {
          const profileResponse = await uploadSignupImage(
            image[0].file,
            SIGNUP_IMAGE_UPLOAD_TYPE_PROFILE
          );
          profileUrl = profileResponse.imageUrl || "";
        } catch (uploadError) {
          console.error("Profile image upload error:", uploadError);
          setToastParams({
            type: "ERROR",
            message: "Failed to upload profile image. Please try again.",
            id: new Date().getTime(),
          });
          setIsLoading(false);
          return;
        }
      }

      // Upload driver license image if provided
      if (driversLicenseUrl && driversLicenseUrl.length > 0 && driversLicenseUrl[0].file) {
        try {
          const licenseResponse = await uploadSignupImage(
            driversLicenseUrl[0].file,
            SIGNUP_IMAGE_UPLOAD_TYPE_DRIVER_LICENSE
          );
          driversLicenseUrlValue = licenseResponse.imageUrl || "";
        } catch (uploadError) {
          console.error("Driver license image upload error:", uploadError);
          setToastParams({
            type: "ERROR",
            message: "Failed to upload driver license image. Please try again.",
            id: new Date().getTime(),
          });
          setIsLoading(false);
          return;
        }
      }

      if (businessLicenseFiles && businessLicenseFiles.length > 0 && businessLicenseFiles[0].file) {
        try {
          const blResponse = await uploadSignupImage(
            businessLicenseFiles[0].file,
            SIGNUP_IMAGE_UPLOAD_TYPE_BUSINESS_LICENSE
          );
          businessLicenseUrlValue = blResponse.imageUrl || "";
        } catch (uploadError) {
          console.error("Business license upload error:", uploadError);
          setToastParams({
            type: "ERROR",
            message: "Failed to upload business license. Please try again.",
            id: new Date().getTime(),
          });
          setIsLoading(false);
          return;
        }
      }

      // Prepare signup data
      const signupData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        companyName: companyName.trim() || "",
        phone: phone.trim(),
        email: email.trim(),
        address: address.trim(),
        city: city.trim(),
        state: state,
        zipcode: zipcode.trim(),
        password: password,
        confirmPassword: confirmPassword,
        profileUrl: profileUrl || "",
        smsNumber: smsNumber.trim() || "",
        notes: notes.trim() || "",
        driversLicense: driversLicense.trim(),
        driversLicenseUrl: driversLicenseUrlValue || "",
        ein: ein.trim() || "",
        businessLicense: businessLicense.trim() || "",
        businessLicenseUrl: businessLicenseUrlValue || "",
      };

      // Call signup API
      const response = await postData(SIGNUP_URL, signupData);

          // Show success message
          setToastParams({
            type: "SUCCESS",
            message: "Account created successfully! Please login.",
            id: new Date().getTime(),
          });

          // Redirect to login page after a short delay
          setTimeout(() => {
            props.history.push("/");
          }, 1500);
    } catch (error) {
      console.error("Signup error:", error);
      setIsLoading(false);
      setToastParams({
        type: "ERROR",
        message:
          error?.response?.data?.error ||
          error?.error ||
          "Signup failed. Please try again.",
        id: new Date().getTime(),
      });
    }
  };

  return (
    <div
      className=""
      style={{
        backgroundImage: `url(${BG})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        width: "100vw",
        minHeight: "100vh",
        height: "100%",
        display: "flex",
        alignItems: isMobile ? "flex-start" : "center",
        justifyContent: "center",
        padding: isMobile ? "10px" : "20px",
        paddingTop: isMobile ? "20px" : "20px",
        paddingBottom: isMobile ? "20px" : "20px",
        overflowY: "auto",
        pointerEvents: isLoading ? "none" : "auto",
      }}
    >
      <style>{`
        .signup-form .forminput-wrap .placeholder {
          margin-bottom: 4px !important;
        }
        .signup-form {
          box-sizing: border-box;
        }
        .signup-form * {
          box-sizing: border-box;
        }
      `}</style>
      <div style={{ 
        position: "relative", 
        width: "100%", 
        maxWidth: isMobile ? "100%" : "1400px",
        marginTop: isMobile ? "0" : "auto",
        marginBottom: isMobile ? "20px" : "auto"
      }}>
        <div className="bg-black box-shadow op-7" style={{ 
          position: "absolute",
          top: isMobile ? "0" : "0",
          left: "0",
          right: "0",
          bottom: "0",
          width: "100%",
          borderRadius: "30px"
        }}></div>
        <form
          onSubmit={onSubmit}
          className="signup-form"
          style={{
            padding: isMobile ? "20px 15px" : "25px 40px",
            paddingBottom: isMobile ? "20px" : "25px",
            borderRadius: "30px",
            width: "100%",
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
            backgroundColor: "#fff",
            boxShadow: "0 5px 10px rgba(0, 0, 0, 0.3)",
            overflow: "visible",
          }}
        >
          <div 
            className="flex flex-middle c-pointer mb-16" 
            onClick={() => props.history.push("/")}
            style={{ 
              position: "absolute",
              top: isMobile ? "15px" : "25px",
              left: isMobile ? "15px" : "25px",
              zIndex: 10
            }}
          >
            <img src={CaretIcon} className="wt-20 rotate-90 op-5" alt="Back" />
            <span className="fs-14 c-white fw-bolder" style={{ marginLeft: "8px" }}>Back to Login</span>
          </div>
          <div className="t-center" style={{ marginBottom: isMobile ? "20px" : "15px" }}>
            {companyLogo && (
              <img 
                src={companyLogo} 
                className={isMobile ? "wt-120 ht-50 mb-12" : "wt-150 ht-60 mb-12"} 
                alt="Company Logo" 
                style={{ maxWidth: "100%" }}
              />
            )}
            <img 
              src={Logo2} 
              className={isMobile ? "wt-120" : "wt-150"} 
              alt="CATTRADER Logo" 
              style={{ maxWidth: "100%" }}
            />
          </div>
          <div className="t-center" style={{ marginBottom: isMobile ? "20px" : "15px" }}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowScanner(true);
              }}
              className="rounded-pill"
              style={{ 
                whiteSpace: 'nowrap', 
                padding: '8px 20px', 
                fontSize: '14px',
                height: '40px',
                lineHeight: '24px',
                backgroundColor: '#4a90e2',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                minWidth: 'auto',
                width: 'auto',
                borderRadius: '20px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
              }}
            >
              📷 Scan Driver License
            </button>
          </div>

          <div>
            <div
              className={`flex flex-between ${isMobile ? "flex-column" : ""}`}
              style={{ gap: "12px", marginBottom: isMobile ? "12px" : "8px" }}
            >
              <FormInput
                inputClassName="rounded-pill bg-blue"
                placeholder="Company Name"
                onChange={(e) => {
                  setCompanyName(e.target.value);
                }}
                className="wt-100p"
                value={companyName}
              />
            </div>

            {/* Row 1: First Name, Last Name, Phone */}
            <div
              className={`flex flex-between ${isMobile ? "flex-column" : ""}`}
              style={{ gap: "12px", marginBottom: isMobile ? "12px" : "8px" }}
            >
              <FormInput
                inputClassName="rounded-pill bg-blue"
                placeholder={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: `First Name <span style='color:red;'>*</span>`,
                    }}
                  ></div>
                }
                onChange={(e) => {
                  setFirstName(e.target.value);
                }}
                className={`${!isMobile ? "wt-33p" : "wt-100p"}`}
                value={firstName}
                hasError={hasSubmitted && (!firstName || firstName.trim().length < 2)}
              />
              <FormInput
                inputClassName="rounded-pill bg-blue"
                placeholder={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: `Last Name <span style='color:red;'>*</span>`,
                    }}
                  ></div>
                }
                onChange={(e) => {
                  setLastName(e.target.value);
                }}
                className={`${!isMobile ? "wt-33p" : "wt-100p"}`}
                value={lastName}
                hasError={hasSubmitted && (!lastName || lastName.trim().length < 2)}
              />
              <FormInput
                inputClassName="rounded-pill bg-blue"
                placeholder={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: `Phone <span style='color:red;'>*</span>`,
                    }}
                  ></div>
                }
                onChange={(e) => {
                  setPhone(normalizeInput(e.target.value));
                }}
                className={`${!isMobile ? "wt-33p" : "wt-100p"}`}
                value={phone}
                hasError={hasSubmitted && !phone}
              />
            </div>

            {/* Row 2: Email, Address, City */}
            <div
              className={`flex flex-between ${isMobile ? "flex-column" : ""}`}
              style={{ gap: "12px", marginBottom: isMobile ? "12px" : "8px" }}
            >
              <div className={`${!isMobile ? "wt-33p" : "wt-100p"}`}>
                <FormInput
                  inputClassName="rounded-pill bg-blue"
                  inputType="login"
                  isBlurEnabled={true}
                  placeholder={
                    <div
                      dangerouslySetInnerHTML={{
                        __html: `Email <span style='color:red;'>*</span>`,
                      }}
                    ></div>
                  }
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  onBlurChanges={handleEmailBlur}
                  value={email}
                  type="email"
                  className="wt-100p"
                  hasError={hasSubmitted && (!email?.trim() || !isValidEmail(email?.trim()))}
                />
              </div>
              <FormInput
                inputClassName="rounded-pill bg-blue"
                placeholder={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: `Address <span style='color:red;'>*</span>`,
                    }}
                  ></div>
                }
                onChange={(e) => {
                  setAddress(e.target.value);
                }}
                className={`${!isMobile ? "wt-33p" : "wt-100p"}`}
                value={address}
                hasError={hasSubmitted && !address}
              />
              <FormInput
                inputClassName="rounded-pill bg-blue"
                placeholder={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: `City <span style='color:red;'>*</span>`,
                    }}
                  ></div>
                }
                onChange={(e) => {
                  setCity(e.target.value);
                }}
                className={`${!isMobile ? "wt-33p" : "wt-100p"}`}
                value={city}
                hasError={hasSubmitted && !city}
              />
            </div>

            {/* Row 3: State, Zipcode, Password */}
            <div
              className={`flex flex-between ${isMobile ? "flex-column" : ""}`}
              style={{ gap: "12px", marginBottom: isMobile ? "12px" : "8px" }}
            >
              <div className={`${!isMobile ? "wt-33p" : "wt-100p"}`}>
                <div className="c-gray fs-14" style={{ marginBottom: "4px" }}>
                  State <span style={{ color: "red" }}>*</span>
                </div>
                <select
                  name="state"
                  className="select font-primary fs-14 fw-bolder rounded-pill bg-blue"
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value);
                  }}
                  style={{ width: "100%" }}
                >
                  <option value="">Select State</option>
                  {(STATES_LIST || []).map((stateOption) => {
                    return (
                      <option key={stateOption} value={stateOption}>
                        {stateOption}
                      </option>
                    );
                  })}
                </select>
                {hasSubmitted && !state && (
                  <div className="fs-10 c-error fw-bold p-absolute">
                    Please select valid state
                  </div>
                )}
              </div>
              <FormInput
                inputClassName="rounded-pill bg-blue"
                placeholder={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: `Zipcode <span style='color:red;'>*</span>`,
                    }}
                  ></div>
                }
                onChange={(e) => {
                  if (isNaN(e.target.value)) {
                    return;
                  }
                  setZipcode((e.target.value || "").replace(/[^\d]/g, ""));
                }}
                className={`${!isMobile ? "wt-33p" : "wt-100p"}`}
                value={(zipcode || "").replace(/[^\d]/g, "")}
                hasError={hasSubmitted && !zipcode}
              />
              <div className={`${!isMobile ? "wt-33p" : "wt-100p"} p-relative`}>
                <FormInput
                  inputClassName="rounded-pill bg-blue"
                  inputType="login"
                  placeholder={
                    <div
                      dangerouslySetInnerHTML={{
                        __html: `Password <span style='color:red;'>*</span>`,
                      }}
                    ></div>
                  }
                  type={seePassword ? "text" : "password"}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  value={password}
                  hasError={hasSubmitted && (!password || password.length < 6)}
                />
                <img
                  src={EYE}
                  style={{ 
                    position: 'absolute',
                    top: isMobile ? '26px' : '30px',
                    right: '12px',
                    cursor: 'pointer',
                    zIndex: 10
                  }}
                  className="wt-25"
                  onClick={(e) => {
                    setSeePassword(!seePassword);
                  }}
                  alt="Toggle password visibility"
                />
              </div>
            </div>

            {/* Row 4: Confirm Password, Notes, SMS Number */}
            <div
              className={`flex flex-between ${isMobile ? "flex-column" : ""}`}
              style={{ gap: "12px", marginBottom: isMobile ? "12px" : "4px" }}
            >
              <div className={`${!isMobile ? "wt-33p" : "wt-100p"} p-relative`}>
                <FormInput
                  inputClassName="rounded-pill bg-blue"
                  inputType="login"
                  placeholder={
                    <div
                      dangerouslySetInnerHTML={{
                        __html: `Confirm Password <span style='color:red;'>*</span>`,
                      }}
                    ></div>
                  }
                  type={seeConfirmPassword ? "text" : "password"}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                  }}
                  value={confirmPassword}
                  hasError={hasSubmitted && password !== confirmPassword}
                />
                <img
                  src={EYE}
                  style={{ 
                    position: 'absolute',
                    top: isMobile ? '26px' : '30px',
                    right: '12px',
                    cursor: 'pointer',
                    zIndex: 10
                  }}
                  className="wt-25"
                  onClick={(e) => {
                    setSeeConfirmPassword(!seeConfirmPassword);
                  }}
                  alt="Toggle confirm password visibility"
                />
              </div>
              <FormInput
                inputClassName="rounded-pill bg-blue"
                placeholder="Notes"
                onChange={(e) => {
                  setNotes(e.target.value);
                }}
                className={`${!isMobile ? "wt-33p" : "wt-100p"}`}
                value={notes}
              />
              <FormInput
                inputClassName="rounded-pill bg-blue"
                placeholder="SMS Number"
                onChange={(e) => {
                  setSmsNumber(normalizeInput(e.target.value));
                }}
                className={`${!isMobile ? "wt-33p" : "wt-100p"}`}
                value={smsNumber}
              />
            </div>

            {/* Row 5: Profile Image, Driver License + upload, EIN + Business license upload */}
            <div
              className={`flex flex-between ${isMobile ? "flex-column" : ""}`}
              style={{ gap: "12px", marginBottom: isMobile ? "8px" : "0px" }}
            >
              <div className={`${!isMobile ? "wt-33p" : "wt-100p"}`}>
                <div className="c-gray fs-14" style={{ marginBottom: "4px" }}>Profile Image</div>
                <div className="flex flex-middle">
                  <ImageUploading onChange={setImage} initialImage={null} />
                </div>
              </div>
              <div className={`${!isMobile ? "wt-33p" : "wt-100p"}`}>
                <FormInput
                  inputClassName="rounded-pill bg-blue"
                  placeholder={
                    <div
                      dangerouslySetInnerHTML={{
                        __html: `Driver License <span style='color:red;'>*</span>`,
                      }}
                    ></div>
                  }
                  onChange={(e) => {
                    setDriversLicense(e.target.value);
                  }}
                  className="wt-100p"
                  value={driversLicense}
                  hasError={hasSubmitted && !driversLicense}
                />
                <div className="c-gray fs-14" style={{ marginBottom: "4px", marginTop: "10px" }}>
                  Driver License Upload
                </div>
                <div className="flex flex-middle">
                  <ImageUploading onChange={setDriversLicenseUrl} initialImage={null} />
                </div>
              </div>
              <div className={`${!isMobile ? "wt-33p" : "wt-100p"}`}>
                <FormInput
                  inputClassName="rounded-pill bg-blue"
                  placeholder="EIN"
                  onChange={(e) => {
                    setEin(e.target.value);
                  }}
                  className="wt-100p"
                  value={ein}
                />
                <FormInput
                  inputClassName="rounded-pill bg-blue"
                  placeholder="Business License"
                  onChange={(e) => {
                    setBusinessLicense(e.target.value);
                  }}
                  className="wt-100p mt-10"
                  value={businessLicense}
                />
                <div className="c-gray fs-14" style={{ marginBottom: "4px", marginTop: "10px" }}>
                  Business License Upload
                </div>
                <div className="flex flex-middle">
                  <ImageUploading onChange={setBusinessLicenseFiles} initialImage={null} />
                </div>
              </div>
            </div>

            {/* Sign In Link and Submit Button */}
            <div className="t-center" style={{ marginTop: isMobile ? "8px" : "4px", marginBottom: isMobile ? "8px" : "4px" }}>
              <span className="fs-12 c-black">
                Already have an account?{" "}
                <Link to="/" className="fs-12 c-pointer c-blue">
                  Sign In
                </Link>
              </span>
            </div>
            <div className="flex flex-between flex-middle" style={{ marginBottom: "0" }}>
              <div></div>
              <Button className="btn">Sign Up</Button>
            </div>
          </div>
        </form>
      </div>
      <Toast
        type={toastParams.type}
        message={toastParams.message}
        id={toastParams.id}
      />
      {isLoading && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
          <ActionLoader />
        </div>
      )}
      {showScanner && (
        <DriverLicenseScanner
          onScanComplete={(parsedData) => {
            // Auto-fill form fields from scanned data
            if (parsedData.firstName) {
              setFirstName(parsedData.firstName);
            }
            if (parsedData.lastName) {
              setLastName(parsedData.lastName);
            }
            if (parsedData.driversLicense) {
              console.log('Setting driver license:', parsedData.driversLicense);
              setDriversLicense(parsedData.driversLicense);
            } else {
              console.warn('No driver license number found in parsed data:', parsedData);
            }
            if (parsedData.address) {
              setAddress(parsedData.address);
            }
            if (parsedData.city) {
              setCity(parsedData.city);
            }
            if (parsedData.state) {
              // State abbreviation to full name mapping
              const stateAbbrMap = {
                'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
                'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
                'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
                'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
                'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
                'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
                'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
                'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
                'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
                'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
                'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
                'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
                'WI': 'Wisconsin', 'WY': 'Wyoming'
              };
              
              const stateValue = parsedData.state.toUpperCase().trim();
              // Check if it's already in STATES_LIST format
              let finalState = STATES_LIST.find(s => s.toUpperCase() === stateValue || s === parsedData.state);
              
              // If not found, try abbreviation mapping
              if (!finalState && stateAbbrMap[stateValue]) {
                finalState = stateAbbrMap[stateValue];
              }
              
              // If still not found, try to find by first 2 letters
              if (!finalState) {
                finalState = STATES_LIST.find(s => s.toUpperCase().substring(0, 2) === stateValue);
              }
              
              if (finalState) {
                setState(finalState);
              } else {
                // Fallback: use the value as-is
                setState(stateValue);
              }
            }
            if (parsedData.zipcode) {
              setZipcode(parsedData.zipcode);
            }
            setShowScanner(false);
          }}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
};

export default Signup;
